import { Injectable, inject } from '@angular/core';

import { CoordinatorStates, Optional } from '@frontend/geo-coordinator/client-models';
import { GeoCoordinatorPermissionsManagerService, PermissionInfo } from '@frontend/geo-coordinator/permissions-manager';
import { ApiServiceExternal } from '@frontend/sports/common/api-utils';
import { GeolocationConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { ModalDialogService } from '@frontend/sports/modal/feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService, LoadingIndicatorHandler, LoadingIndicatorService, NativeAppService, TimerService } from '@frontend/vanilla/core';
import { GeoCoordinationCheckService, GeoCoordinatorClientConfig } from '@frontend/vanilla/features/geo-coordination';
import { once } from 'lodash-es';
import { Observable, firstValueFrom } from 'rxjs';

import { GeolocationCoordinatorService } from './geo-coordinator.service';
import { DeviceInfoResponse, GDSResponse, RetryType } from './geo-location.model';

const loadDownloadAppComponent = () =>
    import(/* webpackChunkName: "ms-download-app" */ './download-native-app/download-app.component').then((m) => m.DownloadAppComponent);
const loadGeoGuardValidationComponent = () =>
    import(/* webpackChunkName: "ms-geo-guard-validation" */ './geo-guard-validation/geo-guard-validation.component').then(
        (m) => m.GeoGuardValidationComponent,
    );
const loadGeoLocationErrorComponent = () =>
    import(/* webpackChunkName: "ms-geo-location-error" */ './geo-location-error/geo-location-error.component').then(
        (m) => m.GeoLocationErrorComponent,
    );
const loadGeoLocationInstallerComponent = () =>
    import(/* webpackChunkName: "ms-geo-location-installer" */ './geo-location-installer/geo-location-installer.component').then(
        (m) => m.GeoLocationInstallerComponent,
    );

@Injectable({ providedIn: 'root' })
export class GeolocationCheckService {
    private readonly vanillaGeolocationService = inject(GeoCoordinationCheckService);
    private readonly geoCoordinatorClientConfig = inject(GeoCoordinatorClientConfig);
    private readonly geolocationCoordinatorService = inject(GeolocationCoordinatorService);
    private readonly modalDialogService = inject(ModalDialogService);
    private readonly deviceService = inject(DeviceService);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly geolocationConfig = inject(GeolocationConfig);
    private readonly user = inject(UserService);
    private readonly loadingIndicator = inject(LoadingIndicatorService);
    private readonly apiServiceExternal = inject(ApiServiceExternal);
    private readonly timerService = inject(TimerService);
    private readonly trackingService = inject(TrackingService);
    private readonly logger = inject(LoggerFactory).getLogger('GeolocationCheckService');
    private readonly geoCoordinatorPermissionsManagerService = inject(GeoCoordinatorPermissionsManagerService);
    private readonly sitecore = inject(Sitecore);

    private isLocationFetched = false;
    private loading: LoadingIndicatorHandler | null;
    private isLocationFetchTimeOut = false;
    private resetTimer: NodeJS.Timeout | null = null;
    private gdsApiInterval = this.geolocationConfig.initialRetryInterval;
    private resetTimerAndRefetchLocation = false;
    private errorResponse: GDSResponse;
    private deviceInfoResponse: DeviceInfoResponse = {};

    geoComplyErrorCodes = this.geolocationConfig.retriableErrorCodes;

    /**
     * Check geolocation restriction and calls the `callback` function at most once.
     * Uses conditional switch between vanilla and legacy implementations.
     */
    async checkGeolocation(
        callback?: (isLocationFailed: boolean) => void,
        options?: {
            waitForLocationAvailable?: boolean;
            showActivator?: boolean;
        },
    ): Promise<void> {
        await firstValueFrom(this.geoCoordinatorClientConfig.whenReady);

        if (this.geoCoordinatorClientConfig.switchOnLegacy ?? true) {
            this.startLegacyGeolocation(callback, options);
        } else {
            this.vanillaGeolocationService.checkGeolocation(callback, options);
        }
    }

    checkGeoLocation$(options: { waitForLocationAvailable: boolean }): Observable<boolean> {
        return new Observable<boolean>((subscriber) => {
            this.checkGeolocation((isLocationFailed) => {
                subscriber.next(isLocationFailed);
                subscriber.complete();
            }, options);
        });
    }

    registerUnload(): void {
        this.vanillaGeolocationService.registerUnload();
    }

    registerSynchronization(): void {
        this.vanillaGeolocationService.registerSynchronization();
    }

    getDeviceInfoResponse(): DeviceInfoResponse {
        return this.geoCoordinatorClientConfig.switchOnLegacy ? this.deviceInfoResponse : this.vanillaGeolocationService.getDeviceInfoResponse();
    }

    private startLegacyGeolocation(
        callback?: (isLocationFailed: boolean) => void,
        options?: {
            waitForLocationAvailable?: boolean;
            showActivator?: boolean;
        },
    ): void {
        const cb = once(callback || (() => {}));

        if (
            this.geolocationConfig.isGeolocationEnabled &&
            this.nativeAppService.isNative &&
            this.user.isAuthenticated &&
            options?.waitForLocationAvailable
        ) {
            this.validateLocation(cb);
        } else {
            if (this.checkBetRestriction()) {
                return;
            }

            if (!this.isGeoLocationRestrictionEnabled()) {
                cb(false);
                return;
            }

            if (!this.loading && !this.isLocationFetched) {
                this.loading = this.loadingIndicator.start();
            }

            this.geolocationCoordinatorService.onInitialized = (initializationStatus: Optional<CoordinatorStates>) => {
                if (initializationStatus === CoordinatorStates.INITIALIZED) {
                    this.stopLoader();

                    if (options?.waitForLocationAvailable) {
                        this.validateLocation(cb);
                    } else {
                        cb(false);
                    }
                }
            };

            this.geolocationCoordinatorService.onUninitialized = () => {
                cb(true);

                if (!this.geolocationConfig.isNDBSEnabled) {
                    if (this.deviceService.isMobile) {
                        this.showLocationConfirm();
                    } else {
                        this.showGeoLocationInstallerDialog();
                    }
                }

                this.stopLoader();
            };

            this.geolocationCoordinatorService.onError = () => {
                this.stopLoader();
            };

            if (!this.geolocationConfig.isNDBSEnabled) {
                this.geolocationCoordinatorService.initialize({ showPLCPopup: false });
                return;
            }

            const permissionsManager = this.geolocationCoordinatorService?.permissionsManager;
            if (!permissionsManager) {
                this.stopLoader();
                cb(false);
                return;
            }

            permissionsManager.onPermissionInfo = (permissionsInfo: PermissionInfo) => {
                if (permissionsInfo.status === 'available') {
                    this.geoCoordinatorPermissionsManagerService.closePermissionsInfo();
                    this.geolocationCoordinatorService.initialize({ showPLCPopup: false });
                    this.stopLoader();
                    return;
                }

                if (permissionsInfo.status === 'notavailable') {
                    this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                        permissionsInfo,
                        textDictionary: <{ [key: string]: string }>(this.sitecore.globalMessages as unknown),
                        geoCoordinatorService: this.geolocationCoordinatorService,
                    });
                    this.stopLoader();
                }
            };

            permissionsManager.seekPermission();
        }
    }

    private stopLoader(): void {
        this.loading?.done();
        this.loading = null;
    }

    private isGeoLocationRestrictionEnabled(): boolean {
        return this.geolocationConfig.isGeolocationEnabled && this.user.isAuthenticated && !this.nativeAppService.isNative;
    }

    private validateLocation(callback: (isLocationFailed: boolean) => void): void {
        this.isLocationFetchTimeOut = false;
        this.isLocationFetched = false;
        this.resetTimerAndRefetchLocation = false;
        this.errorResponse = {} as GDSResponse;
        this.gdsApiInterval = this.geolocationConfig.initialRetryInterval;
        this.getLocationDetailsfromGDS(callback);
        this.startTimer(callback);
    }

    private startTimer(callback: (isLocationFailed: boolean) => void): void {
        this.clearTimer();

        this.resetTimer = this.timerService.setTimeout(() => {
            this.isLocationFetchTimeOut = true;
            if (!this.isLocationFetched) {
                if (this.geolocationConfig.logGenericFailureResults) {
                    this.logGeoLocationError(new Date(), this.errorResponse);
                }
                this.showGeoLocationError();
                callback(true);
            }
        }, this.gdsApiInterval);
    }

    private getLocationDetailsfromGDS(callback: (isLocationFailed: boolean) => void): void {
        if (this.isLocationFetchTimeOut || !this.geolocationConfig.gdsUrl) {
            return;
        }

        const startGDSDate = new Date();
        let payload = `ssoKey=${this.user.ssoToken}`;

        if (this.geolocationConfig.isNDBSEnabled) {
            payload += '&licenseSolution=ndbs';
        }

        this.apiServiceExternal.post<GDSResponse>(this.geolocationConfig.gdsUrl, payload).subscribe({
            next: (result) => this.processGDSResponse(result, callback),
            error: (error) => this.logGeoLocationError(startGDSDate, error),
        });
    }

    private processGDSResponse(result: GDSResponse, callback: (isLocationFailed: boolean) => void): void {
        if (!result?.isLocationAvailable) {
            this.handleUnavailableLocation(result, callback);
            return;
        }

        if (result.locationStatus === 'VALID_LOCATION' && result.errorCode === 0) {
            this.handleValidLocation(result, callback);
        } else {
            this.handleInvalidLocation(result, callback);
        }
    }

    private handleValidLocation(result: GDSResponse, callback: (isLocationFailed: boolean) => void): void {
        this.isLocationFetched = true;
        this.deviceInfoResponse = this.getDeviceResponse(result);
        callback(false);
    }

    private handleInvalidLocation(result: GDSResponse, callback: (isLocationFailed: boolean) => void): void {
        this.errorResponse = result;
        if (this.isRetryableError(result)) {
            this.isLocationFetched = false;
            this.refetchLocationOnRetryableErrorCodes(callback);
            this.refetchGdsApi(callback);
        } else {
            this.clearTimer();
            this.showGeoLocationError();
            callback(true);
        }
    }

    private handleUnavailableLocation(result: GDSResponse, callback: (isLocationFailed: boolean) => void): void {
        this.errorResponse = result;
        this.refetchGdsApi(callback);
    }

    private isRetryableError(result: GDSResponse): boolean {
        return (
            this.geolocationConfig.isRetryButtonEnabled &&
            !!result?.troubleShooterMessages?.tsMsgs?.some((errMsg) => errMsg.retry === RetryType.Retry)
        );
    }

    private refetchLocationOnRetryableErrorCodes(callback: (isLocationFailed: boolean) => void): void {
        if (!this.resetTimerAndRefetchLocation) {
            this.isLocationFetchTimeOut = false;
            this.gdsApiInterval = this.geolocationConfig.retryInterval;
            this.resetTimerAndRefetchLocation = true;
            this.startTimer(callback);
        }
    }

    private clearTimer(): void {
        if (this.resetTimer) {
            this.timerService.clearTimeout(this.resetTimer);
        }
    }

    private refetchGdsApi(callback: (isLocationFailed: boolean) => void): void {
        this.timerService.setTimeout(() => {
            this.getLocationDetailsfromGDS(callback);
        }, 1000);
    }

    private async showGeoLocationError(): Promise<void> {
        this.modalDialogService.openPopup(await loadGeoLocationErrorComponent(), {
            settings: {
                cssClass: 'geo-comply-error-dialog',
                showPageHeader: true,
                fit: false,
                isModalDialog: true,
            },
            data: { errorMessages: this.errorResponse?.troubleShooterMessages?.tsMsgs || [] },
        });
        this.geolocationCoordinatorService.onError = undefined;
        this.geolocationCoordinatorService.onInitialized = undefined;
        this.doTracking();
    }

    private async showGeoLocationInstallerDialog(): Promise<void> {
        this.modalDialogService.openPopup(await loadGeoLocationInstallerComponent(), {
            settings: {
                cssClass: 'geo-location-popup',
                showPageHeader: true,
                fit: false,
                isModalDialog: false,
            },
        });
        this.geolocationCoordinatorService.onError = undefined;
        this.geolocationCoordinatorService.onInitialized = undefined;
    }

    private async showLocationConfirm(): Promise<void> {
        this.modalDialogService.openPopup(await loadGeoGuardValidationComponent(), {
            settings: {
                cssClass: 'geo-guard-validation',
                showPageHeader: true,
                fit: false,
                isModalDialog: false,
            },
        });
        this.geolocationCoordinatorService.onError = undefined;
        this.geolocationCoordinatorService.onInitialized = undefined;
    }

    private async showDownloadAppDialog(): Promise<void> {
        this.modalDialogService.openPopup(await loadDownloadAppComponent(), {
            settings: {
                cssClass: 'native-app-installer',
                showPageHeader: true,
                fit: false,
                isModalDialog: false,
            },
        });
    }

    private checkBetRestriction(): boolean {
        if (
            this.geolocationConfig.isBetRestrictionEnabled &&
            !this.nativeAppService.isNative &&
            this.user.isAuthenticated &&
            !this.geolocationConfig.isNDBSEnabled
        ) {
            this.showDownloadAppDialog();
            return true;
        }
        return false;
    }

    private doTracking(): void {
        this.trackingService.track(trackingConstants.EVENT_GEOLOCATION_ERROR, {
            [trackingConstants.GEOLOCATION_ERRORCODE]: this.errorResponse?.errorCode,
            [trackingConstants.GEOLOCATION_ERRORMESSAGE]: this.errorResponse?.errorMsg,
        });
    }

    private logGeoLocationError(startGDSDate: Date, errorMessage: GDSResponse | any): void {
        if (this.geolocationConfig.logGenericFailureResults) {
            this.logger.error(
                `GDS api url: ${this.geolocationConfig.gdsUrl}, ran for: ${new Date().valueOf() - startGDSDate.valueOf()}ms, error: ${JSON.stringify(errorMessage)}, ssotoken: ${this.user.ssoToken?.substring(0, 100)}`,
            );
        }
    }

    private getDeviceResponse(response: GDSResponse): DeviceInfoResponse {
        return {
            regionCode: response.regionCode,
            deviceUUid: response.deviceUUid,
            deviceModelSolution: response.deviceModelSolution,
            regionName: response.regionName,
            deviceModelOS: response.deviceModelOS,
        };
    }
}
